import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";

import { api, formatAWG } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { usePageSeo } from "@/lib/usePageSeo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal } from "@/components/util/Reveal";

// Category filter chips shown above the grid. `ALL` is always rendered;
// the rest are filtered down to only those with ≥1 active product at
// render time (derived from the live data, not hardcoded) — so if Calvin
// activates Copilot the AI chip appears automatically, and if he
// deactivates every architecture product that chip disappears.
const ALL_CATEGORY_FILTERS = [
  { key: "all", label: "All" },
  { key: "creative", label: "Creative" },
  { key: "architecture", label: "Architecture & 3D" },
  { key: "business", label: "Business" },
  { key: "ai", label: "AI" },
];

// Sales Mode → CTA label. Only "request_quote" leads anywhere real this
// phase; the other two modes exist in the data model for future activation
// (spec constraint) so the CTA renders but routes to the same request flow
// with a soft notice on the detail page. Copy renamed per the Software
// Catalog Expansion phase — "Request Quote" → "Request Pricing" everywhere.
const CTA_LABEL_BY_MODE = {
  request_quote: "Request Pricing",
  online_purchase: "Get Pricing",
  automated_fulfillment: "Get Pricing",
};

// Read the product's categories, tolerating both the new multi-tag array
// AND the legacy singular field so a mid-migration state still renders.
const productCategories = (p) => {
  if (Array.isArray(p.categories) && p.categories.length > 0) return p.categories;
  if (p.category) return [p.category];
  return [];
};

// Card price line — only rendered when a numeric price is populated.
// The empty-price case previously showed "Pricing available on request"
// directly above the CTA, which was redundant with the "Request Pricing"
// button label. Removed per Phase Software Card Copy Standardization;
// the label reappears automatically once a price lands.
const priceLine = (p) => {
  if (p.price && p.price > 0) {
    const label = p.price_label ? ` / ${p.price_label}` : "";
    return `Starting at ${p.price_currency || "AWG"} ${formatAWG(p.price)}${label}`;
  }
  return "";
};

export default function SoftwarePage() {
  const [software, setSoftware] = useState([]);
  const s = useSettings();

  // SEO Phase 3 — exact metadata proposed to owner for review.
  usePageSeo({
    title: "Professional Software — Ur Designs",
    description: "Genuine software licenses and subscriptions for creative, architecture and business workflows — Adobe, Autodesk, Affinity, Microsoft and more, delivered by Ur Designs in Aruba.",
    path: "/software",
  });
  const [searchParams] = useSearchParams();
  // Footer + external deep-links can arrive with `?category=creative`
  // (etc.) to pre-filter the grid. Fallback to "all" for any missing or
  // unrecognised value — the filter list itself is derived from live
  // data so an unknown key would silently render "no results".
  const initialCategory = (searchParams.get("category") || "all").toLowerCase();
  const [activeCategory, setActiveCategory] = useState(initialCategory);

  useEffect(() => {
    api.get("/software").then((r) => setSoftware(r.data));
  }, []);

  // Keep the local state in sync if the URL query changes (e.g. user
  // clicks another footer link without a full remount).
  useEffect(() => {
    const q = (searchParams.get("category") || "all").toLowerCase();
    setActiveCategory(q);
  }, [searchParams]);

  const eyebrow = s.software_eyebrow;
  const heading = s.software_heading;
  const accent = s.software_heading_accent ?? "";
  const description = s.software_hero_description;
  const descriptionMobile = s.software_hero_description_mobile || "";

  const filtered = useMemo(() => {
    if (activeCategory === "all") return software;
    return software.filter((p) => productCategories(p).includes(activeCategory));
  }, [software, activeCategory]);

  // Derive the visible filter chips from the live data — only render a
  // chip when at least one active product carries the tag. `all` always
  // renders. Guarantees the chip strip stays in sync with the catalog as
  // products are activated/deactivated without any manual toggle.
  const activeCategoryKeys = useMemo(() => {
    const set = new Set();
    for (const p of software) {
      for (const c of productCategories(p)) set.add(c);
    }
    return set;
  }, [software]);

  const visibleFilters = useMemo(
    () => ALL_CATEGORY_FILTERS.filter((f) => f.key === "all" || activeCategoryKeys.has(f.key)),
    [activeCategoryKeys],
  );

  return (
    <div data-testid="software-page" className="bg-[#0F0F0F] min-h-screen">
      <PageHero
        testId="software-hero"
        eyebrow={eyebrow}
        heading={heading}
        accent={accent}
        accentInline={!!s.software_accent_inline}
        showDot={!!s.software_show_dot}
        description={description}
        mobileDescription={descriptionMobile}
      />

      {/* Category filter row — same visual language as tier pills elsewhere. */}
      <section
        data-testid="software-category-filters"
        className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-2 pb-8 flex flex-wrap gap-2"
      >
        {visibleFilters.map((f) => {
          const active = activeCategory === f.key;
          return (
            <button
              key={f.key}
              data-testid={`software-filter-${f.key}`}
              onClick={() => setActiveCategory(f.key)}
              className={`px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                active
                  ? "bg-[#FF6600] text-black"
                  : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#FF6600]/40 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </section>

      {/* Product grid */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pb-24">
        {filtered.length === 0 ? (
          <div
            data-testid="software-empty-state"
            className="ur-card p-10 text-center text-[#A3A3A3]"
          >
            No software products in this category yet. Try "All" or check back
            soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 60}>
                <SoftwareProductCard product={p} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// Card mirrors the visual rhythm of the Workstation card
// (logo → name → tagline → description → optional price line → CTA).
// Phase Software Card Copy Standardization: the upper region (logo,
// publisher, name, tagline, short description) is a single clickable
// Link into the detail page; the CTA button remains the sole
// transactional action at the bottom of the card.
const SoftwareProductCard = ({ product: p }) => {
  const ctaLabel = CTA_LABEL_BY_MODE[p.sales_mode] || "Learn More";
  const unavailable = p.availability === "temporarily_unavailable";
  const to = `/software/${p.slug || p.id}`;
  const price = priceLine(p);

  return (
    <div
      data-testid={`software-product-${p.slug || p.id}`}
      className="ur-card p-6 flex flex-col h-full hover:border-[#FF6600]/40 transition-colors"
    >
      <Link
        to={to}
        data-testid={`software-product-${p.slug || p.id}-body`}
        className="group flex flex-col flex-1 rounded-lg -m-1 p-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6600]/50"
      >
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#0F0F0F] border border-[#262626] grid place-items-center shrink-0 overflow-hidden">
            {p.logo ? (
              <img
                src={p.logo}
                alt={p.name}
                className="max-w-[70%] max-h-[70%] object-contain"
              />
            ) : (
              <Sparkles className="w-6 h-6 text-[#FF6600]" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            {/* Vendor label — Class C per the eyebrow inventory: this
                is a product/publisher badge, NOT a section eyebrow, so
                it deliberately does NOT use `.ur-eyebrow` and shows no
                outlined frame. Simple orange uppercase caption,
                subordinate to the product name below it. */}
            <div className="text-[#FF6600] text-xs sm:text-sm font-bold tracking-[0.2em] uppercase leading-none">{p.publisher || p.category}</div>
            <h3 className="font-display text-lg sm:text-xl font-bold mt-1 leading-tight group-hover:text-[#FF6600] transition-colors">
              {p.name}
            </h3>
          </div>
        </div>

        {p.tagline && (
          <div className="mt-5 text-[13px] font-semibold text-white/90">{p.tagline}</div>
        )}
        {p.description_short && (
          <div className="mt-2 text-sm text-[#A3A3A3] leading-relaxed">
            {p.description_short}
          </div>
        )}
      </Link>

      <div className="mt-auto pt-5">
        {price && (
          <div
            data-testid={`software-product-${p.slug || p.id}-price`}
            className="text-xs font-bold uppercase tracking-widest text-[#FF6600] mb-3"
          >
            {price}
          </div>
        )}
        {unavailable ? (
          <button
            disabled
            data-testid={`software-product-${p.slug || p.id}-cta-disabled`}
            className="ur-btn-secondary w-full opacity-60 cursor-not-allowed"
          >
            Temporarily Unavailable
          </button>
        ) : (
          <Link
            to={to}
            data-testid={`software-product-${p.slug || p.id}-cta`}
            className="ur-btn-primary w-full"
          >
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
};
